import { ResolvedCostCode, CostCodeDivisionDoc, CostCodeDoc, CostCodeItemDoc, CostCodeType, CostCodeUtils, DateUtils, DEFAULT_COST_CODES } from 'common';
import { ConcourseDb } from '../../global';
import { projectState } from '../../global/store/project-store';
import { authInterceptor } from '../../global/service/AuthService';

export class CostCodesService {
  static getCostCodesDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-cost-codes`);
  }

  /**
   * Falls back to DEFAULT_COST_CODES when the project's codes db is empty. Joins
   * cost code docs (type: 'cost_code') against division docs (type: 'division') in
   * the same db to produce a hydrated division_name. Docs from a project that hasn't
   * run the 0002-migrate-cost-code-divisions migration yet have no `type` and still
   * carry division_name inline — passed through as-is.
   */
  static resolveCostCodes(docs: (CostCodeDoc | CostCodeDivisionDoc | (ResolvedCostCode & { _id: string }))[] | undefined | null): ResolvedCostCode[] {
    if (!docs || docs.length === 0) return DEFAULT_COST_CODES;

    const divisionById = new Map<string, CostCodeDivisionDoc>();
    const codes: ((CostCodeItemDoc & { division_name?: string }) | (ResolvedCostCode & { _id: string }))[] = [];
    for (const doc of docs as any[]) {
      if (doc.type === CostCodeType.DIVISION) divisionById.set(doc._id, doc);
      else if (doc.type === CostCodeType.COST_CODE || (!doc.type && doc.code_number)) codes.push(doc);
    }
    if (codes.length === 0) return DEFAULT_COST_CODES;

    return codes.map(code => {
      // Legacy (pre-0002-migration) docs have no `type` and already carry code_number/division_name inline.
      const codeNumber = 'code' in code ? code.code : code.code_number;
      if (!('division_id' in code) || !code.division_id) return { ...code, code_number: codeNumber } as ResolvedCostCode;
      const division = divisionById.get(code.division_id);
      return {
        ...code,
        code_number: codeNumber,
        division_name: division ? CostCodeUtils.formatDivisionName(division.division, division.name) : 'Unknown',
      };
    });
  }

  /** One-shot fetch, falling back to DEFAULT_COST_CODES if the project's codes db is empty. */
  static async getCostCodes(projectId: string): Promise<CostCodeDoc[]> {
    const db = CostCodesService.getCostCodesDb(projectId);
    return await db.getDocs();
  }

  /**
   * Gets a signed S3 PUT url for a cost codes CSV/XLSX, scoped to a temp/template path
   * rather than the project's permanent file system.
   */
  static async getUploadUrl(projectId: string, fileName: string): Promise<{ url: string; key: string }> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/${projectId}/settings/upload-temp?fileName=${encodeURIComponent(fileName)}`;
    const response = await authInterceptor.GET(url);
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || json.message || 'Failed to get upload URL');
    }
    return json;
  }

  /**
   * Upserts a single cost code doc directly via PouchDB (_id is an opaque generated id,
   * assigned by the caller for new codes). Uses code.division_id when present; otherwise
   * resolves division_id from projectState.costCodes (already synced; no DB refetch).
   */
  static async saveCostCode(projectId: string, code: ResolvedCostCode & { _id?: string; _rev?: string }): Promise<ResolvedCostCode & { _id: string; _rev: string }> {
    const db = CostCodesService.getCostCodesDb(projectId);
    const { division_name, code_number, division_id: existingDivisionId, ...rest } = code;

    let division_id = existingDivisionId;
    if (!division_id) {
      const divisionNumber = CostCodeUtils.getDivisionNumber(division_name);
      const divisions = projectState.costCodes.filter((doc): doc is CostCodeDivisionDoc => doc.type === CostCodeType.DIVISION);
      const matchedDivision =
        divisions.find(division => CostCodeUtils.formatDivisionName(division.division, division.name) === division_name) ??
        divisions.find(division => division.division === divisionNumber);
      if (!matchedDivision) {
        throw new Error(`Division not found for "${division_name}"`);
      }
      division_id = matchedDivision._id;
    }

    const doc: CostCodeDoc = {
      ...rest,
      _id: code._id ?? ConcourseDb.makeShortDocId(),
      type: CostCodeType.COST_CODE,
      code: code_number,
      division_id,
    };
    const saved = await db.saveDoc(doc);
    return { ...saved, code_number: saved.code, division_name, division_id };
  }

  /** Soft-deletes division and/or cost code docs (mixed) by stamping deleted_date/deleted_by. */
  static async softDeleteCostCodeDocs(projectId: string, docs: CostCodeDoc[], userId: string): Promise<void> {
    if (docs.length === 0) return;
    const db = CostCodesService.getCostCodesDb(projectId);
    await db.saveDocs(docs.map(doc => ({ ...doc, deleted_by: userId, deleted_date: DateUtils.currentIso })));
  }

  /** Upserts a division doc directly via PouchDB (_id is an opaque generated id, assigned by the caller for new divisions). */
  static async saveDivision(projectId: string, division: { _id?: string; division: string; name: string }): Promise<CostCodeDivisionDoc> {
    const db = CostCodesService.getCostCodesDb(projectId);
    const doc: CostCodeDivisionDoc = { _id: division._id ?? ConcourseDb.makeShortDocId(), type: CostCodeType.DIVISION, division: division.division, name: division.name };
    return await db.saveDoc(doc);
  }

  /**
   * Uploads a previously-uploaded file (identified by its S3 key) for bulk import.
   * The backend downloads and parses the file (CSV/XLSX) and upserts codes matched by code_number.
   */
  static async bulkUploadCostCodes(projectId: string, s3Key: string): Promise<{ message: string; count: number }> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/${projectId}/settings/cost-codes/bulk-upload`;
    const response = await authInterceptor.POST(url, { s3Key });
    const json = await response.json().catch(() => ({ message: response.statusText }));
    if (!response.ok) {
      throw new Error('There was an error uploading the cost codes: ' + (json.error || json.message));
    }
    return json;
  }
}
